import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Grid,
  Chip,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { addDays } from 'date-fns';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useSkills } from '../../contexts/SkillsContext';
import { useOnCalls } from '../../contexts/OnCallsContext';
import { useAbsences } from '../../contexts/AbsencesContext';
import TechnicianSelect from '../technicians/TechnicianSelect';
import { validateOnCall, ValidationErrorType } from '../../services/onCallValidation';

const validationSchema = yup.object({
  technicianId: yup.string().required('Le technicien est requis'),
  startDate: yup.date().required('La date de début est requise'),
  endDate: yup.date()
    .required('La date de fin est requise')
    .min(yup.ref('startDate'), 'La date de fin doit être après la date de début'),
  skills: yup.array()
    .min(1, 'Au moins une compétence est requise')
    .required('Les compétences sont requises'),
  bypassAbsenceValidation: yup.boolean()
});

const OnCallForm = ({ onCall, onClose }) => {
  const { technicians, getTechnicianById } = useTechnicians();
  const { skills } = useSkills();
  const { onCalls, addOnCall, updateOnCall } = useOnCalls();
  const { absences } = useAbsences();
  
  const [technicianSkills, setTechnicianSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  
  // Référence pour le champ Date de fin
  const endDateFieldRef = useRef(null);

  const handleSubmitWithValidation = async (values, { setSubmitting }) => {
    try {
      // Normaliser les dates au format YYYY-MM-DD en conservant la timezone locale
      const formatDateToLocalString = (date) => {
        if (!(date instanceof Date)) return date;
        // Utiliser la timezone locale au lieu d'UTC pour éviter les décalages
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const normalizedValues = {
        ...values,
        startDate: formatDateToLocalString(values.startDate),
        endDate: formatDateToLocalString(values.endDate)
      };

      // Adapter validateOnCall pour utiliser les données des contextes et le bypass
      const validation = await validateOnCall(
        normalizedValues, 
        onCalls, 
        absences, 
        onCall?.id,
        normalizedValues.bypassAbsenceValidation // Passer le flag de bypass
      );

      if (!validation.isValid) {
        if (validation.type === ValidationErrorType.JTL_WARNING) {
          setPendingValues(normalizedValues);
          setShowOverrideDialog(true);
          return;
        }
        setValidationError(validation);
        return;
      }

      if (onCall?.id) {
        await updateOnCall(onCall.id, normalizedValues);
      } else {
        await addOnCall(normalizedValues);
      }
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'astreinte:', error);
      setValidationError({
        isValid: false,
        message: 'Une erreur est survenue lors de l\'enregistrement.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      technicianId: onCall?.technicianId || '',
      startDate: onCall?.startDate ? new Date(onCall.startDate) : null,
      endDate: onCall?.endDate ? new Date(onCall.endDate) : null,
      skills: onCall?.skills || [],
      bypassAbsenceValidation: onCall?.bypassAbsenceValidation || false
    },
    validationSchema,
    onSubmit: handleSubmitWithValidation
  });

  useEffect(() => {
    const loadTechnicianSkills = (technicianId) => {
      try {
        const technician = getTechnicianById(parseInt(technicianId));
        if (technician && technician.skills) {
          // Normaliser les compétences: supporter [id] ou [{skillId, level}|{id}]
          const techSkillIds = (technician.skills || []).map((item) =>
            typeof item === 'object' ? (item.skillId ?? item.id) : item
          );
          const techSkills = skills
            .filter(skill => techSkillIds.includes(skill.id))
            .sort((a, b) => a.name.localeCompare(b.name));
          setTechnicianSkills(techSkills);
          setSelectedSkills(techSkills);
          formik.setFieldValue('skills', techSkills.map(skill => skill.id));
        } else {
          setTechnicianSkills([]);
          setSelectedSkills([]);
          formik.setFieldValue('skills', []);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des compétences:', error);
      }
    };

    if (formik.values.technicianId && skills.length > 0) {
      loadTechnicianSkills(formik.values.technicianId);
    }
  }, [formik.values.technicianId, skills, getTechnicianById]);

  const handleTechnicianChange = (event) => {
    formik.setFieldValue('technicianId', event.target.value);
    setValidationError(null);
  };

  const handleStartDateChange = (date) => {
    formik.setFieldValue('startDate', date);
    setValidationError(null);
    // Calculer automatiquement la date de fin (jeudi suivant)
    const endDate = addDays(date, 6); // +6 jours pour aller jusqu'au jeudi suivant
    formik.setFieldValue('endDate', endDate);
    
    // Focus automatique sur le champ Date de fin après une courte pause
    setTimeout(() => {
      if (endDateFieldRef.current) {
        // Chercher le bouton d'ouverture du calendrier (icône)
        const calendarButton = endDateFieldRef.current.querySelector('button[aria-label*="calendar"], button[aria-label*="calendrier"], .MuiIconButton-root');
        if (calendarButton) {
          calendarButton.click();
        } else {
          // Fallback : essayer de déclencher le focus et clic sur l'input
          const inputElement = endDateFieldRef.current.querySelector('input');
          if (inputElement) {
            inputElement.focus();
            inputElement.click();
          }
        }
      }
    }, 150);
  };

  const handleSkillDelete = (skillToDelete) => {
    const updatedSkills = selectedSkills.filter(skill => skill.id !== skillToDelete.id);
    setSelectedSkills(updatedSkills);
    formik.setFieldValue('skills', updatedSkills.map(skill => skill.id));
  };

  const handleOverrideConfirm = async () => {
    if (pendingValues) {
      try {
        if (onCall?.id) {
          await updateOnCall(onCall.id, pendingValues);
        } else {
          await addOnCall(pendingValues);
        }
        onClose();
      } catch (error) {
        console.error('Erreur lors de l\'enregistrement de l\'astreinte:', error);
      }
    }
    setShowOverrideDialog(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 0,
          overflow: 'hidden'
        }}
      >
        <Box 
          component="form" 
          onSubmit={formik.handleSubmit} 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 3,
            pt: 6,
            '& .MuiFormControl-root': {
              my: 0.5
            }
          }}
        >
          {validationError && (
            <Alert 
              severity={validationError.type === ValidationErrorType.JTL_WARNING ? "warning" : "error"}
              sx={{ mb: 2 }}
            >
              {validationError.message}
            </Alert>
          )}

          <TechnicianSelect
            value={formik.values.technicianId}
            onChange={handleTechnicianChange}
            error={formik.touched.technicianId && Boolean(formik.errors.technicianId)}
            helperText={formik.touched.technicianId && formik.errors.technicianId}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Date de début (vendredi)"
                value={formik.values.startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                    helperText={formik.touched.startDate && formik.errors.startDate}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Date de fin (jeudi)"
                value={formik.values.endDate}
                onChange={(date) => {
                  formik.setFieldValue('endDate', date);
                  setValidationError(null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    ref={endDateFieldRef}
                    fullWidth
                    error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                    helperText={formik.touched.endDate && formik.errors.endDate}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Compétences d'astreinte
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {selectedSkills.map((skill) => (
                <Chip
                  key={skill.id}
                  label={`${skill.name} (${skill.coverageIndex}%)`}
                  onDelete={() => handleSkillDelete(skill)}
                  sx={{
                    backgroundColor: `${skill.color}20`,
                    border: `1px solid ${skill.color}40`,
                    '& .MuiChip-label': {
                      color: 'text.primary'
                    },
                    '& .MuiChip-deleteIcon': {
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'text.primary'
                      }
                    }
                  }}
                />
              ))}
            </Stack>
            {formik.touched.skills && formik.errors.skills && (
              <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                {formik.errors.skills}
              </Typography>
            )}
          </Box>

          {/* Case à cocher pour bypass des absences */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="bypassAbsenceValidation"
                  checked={formik.values.bypassAbsenceValidation}
                  onChange={formik.handleChange}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    Bypass exceptionnel des absences
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Permet l'affectation malgré des absences normalement bloquantes (accord manager requis)
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'flex-end', 
            mt: 2,
            '& .MuiButton-root': {
              minWidth: 100
            }
          }}>
            <Button onClick={onClose}>
              Annuler
            </Button>
            <Button 
              variant="contained" 
              type="submit"
              disabled={formik.isSubmitting}
            >
              {onCall ? 'Modifier' : 'Créer'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog open={showOverrideDialog} onClose={() => setShowOverrideDialog(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <Typography>
            Le technicien a un JTL pendant cette période. Voulez-vous quand même créer l'astreinte ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOverrideDialog(false)}>Annuler</Button>
          <Button onClick={handleOverrideConfirm} variant="contained" color="warning">
            Confirmer quand même
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default OnCallForm;